import { createClient } from 'contentful';
import { takeLatest, call, put } from 'redux-saga/effects';
import _get from 'lodash/get';
import _some from 'lodash/some';
import { post } from 'utils/api';
import Config from 'utils/getEnvConfig';
import {
  SUBMIT_ADMIN_ASSESSMENT,
  GET_ADMIN_ASSESSMENTS,
  GET_FULL_ADMIN_ASSESSMENTS,
} from './constants';
import {
  processing,
  getAdminAssessmentsResult,
  getFullAdminAssessmentsResult,
  submitAdminAssessmentResult,
} from './actions';

const {
  CONTENTFUL: { SPACE_ID, PREVIEW_ACCESS_TOKEN, PREVIEW_CDN_URL, ENVIRONMENT },
  LAMBDA: { ADMIN_ASSESSMENT_UPDATE },
} = Config;

const ctfClient = createClient({
  space: SPACE_ID,
  accessToken: PREVIEW_ACCESS_TOKEN,
  host: PREVIEW_CDN_URL,
  environment: ENVIRONMENT,
  retryOnError: false,
});

function* getAdminAssessmentsSaga({ payload = 'en-US' }) {
  try {
    yield put(processing(true));

    const localeFilters = {};
    if (payload) {
      localeFilters.locale = payload;
    }
    const response = yield call(() =>
      ctfClient.getEntries({
        content_type: 'assessments',
        ...localeFilters,
      }),
    );
    const assessments = response.items.map(
      ({
        sys: { id },
        fields: { name, assessment, translationLink, reviewStatus },
      }) => {
        const translationLinkFields = _get(translationLink, 'fields', null);

        const parsedAssessmentJson =
          _get(translationLinkFields, 'assessment') || assessment;
        const finalAssessmentJson =
          parsedAssessmentJson != null ? parsedAssessmentJson.json || '' : '';

        let assessmentObject;
        try {
          assessmentObject =
            finalAssessmentJson !== '' ? JSON.parse(finalAssessmentJson) : {};
        } catch (e) {
          assessmentObject = {};
        }
        const hasGenderQuestion = assessmentObject.pages
          ? _some(assessmentObject.pages, page =>
              _some(
                page.elements,
                question => question.name === 'genderStandard',
              ),
            )
          : false;
        const hasAgeQuestion = assessmentObject.pages
          ? _some(assessmentObject.pages, page =>
              _some(
                page.elements,
                question => question.name === 'ageRangeStandard',
              ),
            )
          : false;
        return {
          id,
          name: name != null ? name || 'Unnamed' : 'Unnamed',
          passed: hasGenderQuestion && hasAgeQuestion,
          assessment: finalAssessmentJson,
          reviewStatus: reviewStatus != null ? reviewStatus || '' : '',
        };
      },
    );
    yield put(getAdminAssessmentsResult(assessments));
  } catch (error) {
    yield put(getAdminAssessmentsResult([]));
  } finally {
    yield put(processing(false));
  }
}

function* getFullAdminAssessmentsSaga() {
  try {
    yield put(processing(true));

    const response = yield call(() =>
      ctfClient.getEntries({
        content_type: 'assessments',
      }),
    );
    yield put(getFullAdminAssessmentsResult(response));
  } catch (error) {
    yield put(getFullAdminAssessmentsResult({}));
  } finally {
    yield put(processing(false));
  }
}

function* submitAdminAssessmentSaga({ payload }) {
  try {
    yield put(processing(true));
    const { id, data, locales = ['en-US'] } = payload;

    yield call(
      post,
      ADMIN_ASSESSMENT_UPDATE.URL,
      {
        id,
        json: JSON.stringify(JSON.parse(data)),
        locales,
      },
      {
        headers: {
          'x-api-key': ADMIN_ASSESSMENT_UPDATE.API_KEY,
        },
      },
    );

    yield put(
      submitAdminAssessmentResult({
        error: false,
      }),
    );
  } catch (error) {
    yield put(
      submitAdminAssessmentResult({
        error: true,
        message: 'Something went wrong. Try again later.',
      }),
    );
  } finally {
    yield put(processing(false));
  }
}

// Individual exports for testing
export default function* defaultSaga() {
  yield takeLatest(GET_ADMIN_ASSESSMENTS, getAdminAssessmentsSaga);
  yield takeLatest(GET_FULL_ADMIN_ASSESSMENTS, getFullAdminAssessmentsSaga);
  yield takeLatest(SUBMIT_ADMIN_ASSESSMENT, submitAdminAssessmentSaga);
}
